import { supabase } from './supabase'
import {
  buildContentFamiliaritySummary,
  classifyWordFamiliarity,
  extractReaderCharacters,
  extractReaderGrammarConceptIds,
  extractReaderWordIds,
  learningStatusToWordFamiliarity,
  learningStatusToGrammarFamiliarity,
  type CharacterFamiliarityStatus,
  type ContentFamiliaritySummary,
  type GrammarFamiliarityStatus,
  type StudyCardProgressLike,
  type WordFamiliarityStatus
} from './familiarity-core'
import {
  getLearningContentState,
  getLearningContentStates,
  type LearningContentState
} from './learning-state'
import {
  characterLearningLocator,
  grammarLearningLocator,
  storyLearningLocator,
  wordLearningLocator,
  type LearningContentLocator
} from './learning-state-core'
import type { ReaderContent } from '@/types/reader'

type StudyNoteCardsRow = {
  dict_entry_id: number | null
  card?: StudyCardProgressLike | StudyCardProgressLike[] | null
}

function relationArray<T>(value: T | T[] | null | undefined): T[] {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

function uniqueNumbers(values: number[]) {
  return Array.from(new Set(values)).sort((a, b) => a - b)
}

function uniqueStrings(values: string[]) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  )
}

export async function getWordFamiliarityStatuses(
  entryIds: number[]
): Promise<Map<number, WordFamiliarityStatus>> {
  const ids = uniqueNumbers(entryIds)
  const statuses = new Map<number, WordFamiliarityStatus>(
    ids.map((id) => [id, 'new'])
  )
  if (!ids.length) return statuses

  const learningStates = await getLearningContentStates(
    ids.map(wordLearningLocator)
  )
  ids.forEach((id) => {
    const state = learningStates.get(`word:${id}`)
    statuses.set(id, learningStatusToWordFamiliarity(state?.status))
  })

  const { data, error } = await supabase
    .from('study_notes')
    .select(
      'dict_entry_id, card:study_cards!inner(id, state, reps, last_review, deck_cards:study_deck_cards!inner(card_id))'
    )
    .eq('note_type', 'word')
    .in('dict_entry_id', ids)

  if (error) throw error
  ;((data ?? []) as StudyNoteCardsRow[]).forEach((row) => {
    if (typeof row.dict_entry_id !== 'number') return
    statuses.set(
      row.dict_entry_id,
      classifyWordFamiliarity(relationArray(row.card))
    )
  })

  return statuses
}

export async function getCharacterFamiliarityStatuses(
  characters: string[]
): Promise<Map<string, CharacterFamiliarityStatus>> {
  const ids = uniqueStrings(characters)
  const statuses = new Map<string, CharacterFamiliarityStatus>(
    ids.map((id) => [id, 'new'])
  )
  if (!ids.length) return statuses

  const states = await getLearningContentStates(
    ids.map(characterLearningLocator)
  )
  ids.forEach((id) => {
    if (states.get(`character:${id}`)?.status) {
      statuses.set(id, 'seen')
    }
  })

  return statuses
}

export async function getGrammarFamiliarityStatuses(
  conceptIds: string[]
): Promise<Map<string, GrammarFamiliarityStatus>> {
  const ids = uniqueStrings(conceptIds)
  const statuses = new Map<string, GrammarFamiliarityStatus>(
    ids.map((id) => [id, 'new'])
  )
  if (!ids.length) return statuses

  const locators = ids.map(grammarLearningLocator)
  const states = await getLearningContentStates(locators)
  ids.forEach((id) => {
    const state = states.get(`grammar:${id}`)
    statuses.set(id, learningStatusToGrammarFamiliarity(state?.status))
  })
  return statuses
}

export async function getSavedGrammarExampleCount(
  conceptId: string
): Promise<number> {
  const { count, error } = await supabase
    .from('study_notes')
    .select('id', { count: 'exact', head: true })
    .eq('note_type', 'sentence')
    .contains('tags', ['grammar', `grammar:${conceptId}`])

  if (error) throw error
  return count ?? 0
}

export async function getContentFamiliaritySummary(
  content: ReaderContent
): Promise<ContentFamiliaritySummary> {
  const wordIds = extractReaderWordIds(content)
  const characterTexts = extractReaderCharacters(content)
  const grammarIds = extractReaderGrammarConceptIds(content)
  const storySlug =
    content.sourceType === 'story' ? (content.metadata?.slug ?? null) : null

  const [wordStatuses, characterStatuses, grammarStatuses, storyState] =
    await Promise.all([
      getWordFamiliarityStatuses(wordIds),
      getCharacterFamiliarityStatuses(characterTexts),
      getGrammarFamiliarityStatuses(grammarIds),
      storySlug
        ? getLearningContentState(storyLearningLocator(storySlug))
        : Promise.resolve(null as LearningContentState | null)
    ])

  return buildContentFamiliaritySummary({
    contentType: content.sourceType === 'story' ? 'story' : 'reader',
    contentId: storySlug ?? content.id,
    wordIds,
    wordStatuses,
    characterTexts,
    characterStatuses,
    grammarConceptIds: grammarIds,
    grammarStatuses,
    progressRatio: storyState?.progress ?? null
  })
}

export async function getLearningStatesForLocators(
  locators: LearningContentLocator[]
) {
  return getLearningContentStates(locators)
}
